import { registerPlugin } from '@capacitor/core';
import { hatPlugin, istNativeApp } from './nativePlatform';
import { sitzungImLaufMerker, type AuthBeobachtbar, type BiometrieStand } from './biometrieLogik';

/**
 * Native-Plugin `Biometrie` der Android-Hülle (docs/android-app-vertrag.md,
 * „Native-Plugin Biometrie"): Anmeldung mit Fingerabdruck oder Gesicht.
 *
 * Registriert wird erst beim ersten Zugriff — im Browser nie. Ob die
 * installierte APK das Plugin kennt, sagt `biometrieVerfuegbar()` (ab 1.0.3).
 * Ablehnungen tragen `code` (`biometrieFehlerAus` in biometrieLogik.ts);
 * `biometrie_geaendert` heißt: Finger/Gesichter auf dem Gerät haben sich
 * geändert, der Eintrag ist gelöscht.
 */
export interface BiometriePlugin {
  pruefen(): Promise<BiometrieStand>;
  /** Mit Abfrage; `bestaetigen: false` nur für die bereits gespeicherte E-Mail (Passwort nachziehen). */
  speichern(opts: { email: string; passwort: string; bestaetigen?: boolean }): Promise<void>;
  /** Wächter prüfen, Abfrage, danach E-Mail und Passwort. */
  lesen(): Promise<{ email: string; passwort: string }>;
  /** Eintrag, Schlüssel und Wächter. */
  loeschen(): Promise<void>;
}

/**
 * Gab es in diesem App-Lauf schon eine Sitzung (Start mit Sitzung, Anmeldung)? Dann fragt die
 * Anmeldemaske nicht mehr von selbst (`biometrieAutomatischFragen`). Abgemeldet wird in App.tsx;
 * deshalb hört der Merker selbst auf `onAuthStateChange` (`beobachteSitzungImLauf`, ab dem Laden).
 */
const sitzungImLauf = sitzungImLaufMerker();

export const sitzungImLaufGesehen = (): boolean => sitzungImLauf.gesehen();
export const merkeSitzungImLauf = (): void => sitzungImLauf.merke();
export const beobachteSitzungImLauf = (auth: AuthBeobachtbar | null | undefined): void => sitzungImLauf.beobachte(auth);

let plugin: BiometriePlugin | null = null;

export function biometrie(): BiometriePlugin {
  if (!plugin) plugin = registerPlugin<BiometriePlugin>('Biometrie');
  return plugin;
}

/** App UND eine APK, die das Plugin kennt. */
export function biometrieVerfuegbar(): boolean {
  return istNativeApp() && hatPlugin('Biometrie');
}

/** Stand des Geräts; `null` im Browser, bei alter APK oder wenn die Brücke scheitert. Wirft nie. */
export async function biometrieStand(): Promise<BiometrieStand | null> {
  if (!biometrieVerfuegbar()) return null;
  try {
    const s = await biometrie().pruefen();
    return { verfuegbar: !!s.verfuegbar, grund: s.grund, gespeichert: !!s.gespeichert, email: s.email ?? null };
  } catch (e) {
    console.warn('[biometrie] Stand nicht lesbar:', e);
    return null;
  }
}
